import { Col, Divider, Form, Row } from 'antd';
import { Rule } from 'antd/es/form';
import { OptionEntity } from 'domain/entities/OptionEntity';
import { openErrorNoti } from 'presentation/components/Notification';
import { FormInput } from 'presentation/components/form/FormInput.component';
import { FormRadio } from 'presentation/components/form/FormRadio.component';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { useEffect, useState } from 'react';
import { MASTER_CATEGORY_LIST } from 'utilities/constants/COMMON';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import { StepProps } from '../props/StepProps';
import { ButtonList } from './ButtonList';

const INPUT_TEXT_MAX_LENGTH = 100;

export const PurchaseStepOne = ({
    data,
    masterData,
    next,
    setOfferId,
    setIsChange,
    ...props
}: StepProps) => {
    const [isShowAddress, setIsShowAddress] = useState<boolean>(false);
    const [btnDisable, setBtnDisable] = useState<boolean>(true);
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();

    useEffect(() => {
        props.scrollTop && props.scrollTop();
    }, []);

    useEffect(() => {
        if (data && masterData) {
            //setDefaultvalue
            if (!data?.designatedAddressDiv) {
                const defaultOption = masterData[
                    MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION
                ]?.find((item: OptionEntity) => item.isDefault);
                data.designatedAddressDiv = defaultOption?.value ?? '';
            }
            form.setFieldsValue(data);
            onChangeForm();
        }
        if (data?.designatedAddressDiv == '99') {
            setIsShowAddress(true);
        } else {
            setIsShowAddress(false);
        }
    }, [data, masterData]);

    const onChangeForm = () => {
        form.validateFields({
            validateOnly: true,
        })
            .then(() => {
                setBtnDisable(false);
            })
            .catch((err) => {
                setBtnDisable(err.errorFields.length > 0);
            });
    };
    const postcodeValidatorRule = (label?: string, message?: string): Rule => ({
        // @ts-expect-error custom id
        id: 'postcode',
        validator(_: any, value?: string) {
            const v = (value ?? '').replace(/\D/g, '');
            if (!v) return Promise.reject(new Error(getMessage('In_Required', label)));
            if (v.length !== 7) return Promise.reject(new Error(message ?? getMessage('Postcode_Invalid')));
            return Promise.resolve();
        },
    });

    const phoneValidatorRule = (label?: string, message?: string): Rule => ({
        // @ts-expect-error custom id
        id: 'phone',
        validator(_: any, value?: string) {
            const v = (value ?? '').replace(/\D/g, '');
            if (!v) return Promise.reject(new Error(getMessage('In_Required', label)));
            if (v.length < 10 || v.length > 11) {
            return Promise.reject(new Error(message ?? '10〜11桁で入力してください'));
            }
            return Promise.resolve();
        },
    });
    const submitNext = async () => {
        try {
            dispatch(startFetching());
            const payload = {
                ...form.getFieldsValue(),
                representativePhoneNumber: commonHelpers.toNumberOnly(
                    form.getFieldValue('representativePhoneNumber'),
                ),
                picPhoneNumber: commonHelpers.toNumberOnly(form.getFieldValue('picPhoneNumber01')),
                picPhoneExtNumber: form.getFieldValue('picPhoneExt01'),
                companyPostalCode: commonHelpers.toNumberOnly(
                    form.getFieldValue('companyPostalCode'),
                ),
                designatedAddressPostalCode: isShowAddress
                    ? commonHelpers.toNumberOnly(form.getFieldValue('designatedAddressPostalCode'))
                    : '',
                designatedAddress: isShowAddress ? form.getFieldValue('designatedAddress') : '',
                designatedAddressNotes: isShowAddress
                    ? form.getFieldValue('designatedAddressNotes')
                    : '',
                picEmail: form.getFieldValue('picEmail01'),
                procedureId: data?.procedureId,
                purchaseOfferId: data?.purchaseOfferId ?? '',
            };
            const response = await props.purchaseUseCase.savePurchaseCompanyStep1(payload);
            if (response) {
                setOfferId && setOfferId(response.result?.purchaseOfferId ?? '');
                next && next();
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const changeBtnDisable = (isError: any) => {
        if (isError) {
            setBtnDisable(true);
        } else {
            onChangeForm();
        }
    };

    return (
        <>
            <Form
                form={form}
                onValuesChange={() => {
                    onChangeForm();
                    setIsChange && setIsChange(true);
                }}
                onFinish={submitNext}
            >
                <Row className="min-w-min">
                    <div className="w-full mb-[48px]">
                        <span className="content-step-form heading-step-form">会社情報</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="会社名"
                                    name="companyName"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    formatNumber={false}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="会社名（カナ）"
                                    name="companyNameKana"
                                    required
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="会社の郵便番号（ハイフンなし）"
                                    name="companyPostalCode"
                                    maxLength={7}
                                    formatNumber={false}
                                    type="postcode"
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[postcodeValidatorRule('会社の郵便番号')]}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="会社の所在地"
                                    name="companyAddress"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="代表者名"
                                    name="representativeName"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="代表者名（カナ）"
                                    name="representativeNameKana"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="会社の電話番号（ハイフンなし）"
                                    name="representativePhoneNumber"
                                    type="phone"
                                    required
                                    maxLength={12}
                                    formatNumber={false}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[phoneValidatorRule('会社の電話番号')]}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="担当者氏名"
                                    name="picName"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="担当者氏名（カナ）"
                                    name="picNameKana"
                                    required
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="担当者の連絡先（ハイフンなし）"
                                    name="picPhoneNumber01"
                                    required
                                    type="phone"
                                    maxLength={12}
                                    formatNumber={false}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[phoneValidatorRule('担当者の連絡先')]}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="担当者の連絡先（内線）"
                                    name="picPhoneExt01"
                                    type="number"
                                    formatNumber={false}
                                    required
                                    maxLength={12}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[phoneValidatorRule('担当者の連絡先')]}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="担当者のメールアドレス"
                                    name="picEmail01"
                                    required
                                    type="email"
                                    maxLength={255}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    rules={[
                                        {
                                            type: 'email',
                                            message: getMessage('Mail_Invalid'),
                                        },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </div>
                    <div className="w-full mb-[48px]">
                        <span className="content-step-form heading-step-form">送付先</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={24} className="pr-[12px]">
                                <FormRadio
                                    label="送付先区分"
                                    name="designatedAddressDiv"
                                    options={
                                        masterData
                                            ? masterData[
                                                  MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION
                                              ]
                                            : []
                                    }
                                    required
                                    onChange={(e) => {
                                        if (e == '99') {
                                            setIsShowAddress(true);
                                        } else {
                                            setIsShowAddress(false);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="mb-6">
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="送付先の郵便番号（ハイフンなし）"
                                    name="designatedAddressPostalCode"
                                    required={isShowAddress}
                                    disabled={!isShowAddress}
                                    type="postcode"
                                    formatNumber={false}
                                    maxLength={7}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    currentForm={form}
                                    onValidateError={changeBtnDisable}
                                    rules={isShowAddress ? [postcodeValidatorRule('送付先の郵便番号')] : []}
                                />
                            </Col>
                            <Col span={12} className="pl-[12px]">
                                <FormInput
                                    label="送付先の住所"
                                    name="designatedAddress"
                                    required={isShowAddress}
                                    disabled={!isShowAddress}
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    currentForm={form}
                                    onValidateError={changeBtnDisable}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="送付先の名称（事業者名等）"
                                    name="designatedAddressOfficeName"
                                    required={isShowAddress}
                                    disabled={!isShowAddress}
                                    maxLength={INPUT_TEXT_MAX_LENGTH}
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    currentForm={form}
                                    onValidateError={changeBtnDisable}
                                />
                            </Col>
                        </Row>
                    </div>
                    <ButtonList {...props} next={submitNext} disable={btnDisable} />
                </Row>
            </Form>
        </>
    );
};
